import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, EMPTY, of } from 'rxjs';
import { catchError, concatMap, filter, map, tap } from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';

import {
  FEED_POPULATE_TYPE,
  FEED_UPDATE_TYPE,
  FeedAction,
  FeedInit,
  FeedUpdate,
} from './feed.actions';
import { Feedback } from '@libs/src/feedback/feedback';
import { Group } from '@libs/src/models/group.model';
import { Match, MatchConfig } from '@libs/src/models/match.interfaces';

import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';

@Injectable()
export class FeedEffects {
  populateMainFeed = createEffect(() =>
    this.actions.pipe(
      ofType(FEED_POPULATE_TYPE),
      concatMap((action: FeedAction) => {
        const group = action.payload.group;
        return combineLatest([
          this.getCurrentMatchConfig(group),
          this.accountBackend.getAccountGroupsMatchCyclePreview(group),
        ]).pipe(
          concatMap(([matchConfig, matchCyclePreviews]) => {
            if (isEmpty(matchConfig)) {
              return of(
                FeedInit({
                  group,
                  update: { matchConfigs: {}, matchCycles: {} },
                }),
              );
            }
            return this.getAccountMatchCycles(group, matchConfig.items[0]).pipe(
              catchError(() => EMPTY),
              map((matchCycles) =>
                FeedInit({
                  group,
                  update: {
                    matchConfigs: matchConfig,
                    matchCycles,
                    matchCyclePreview: get(
                      matchCyclePreviews,
                      'matchCyclePreviews[0]',
                    ),
                  },
                }),
              ),
            );
          }),
        );
      }),
    ),
  );

  populateCurrentMatch = createEffect(() =>
    this.actions.pipe(
      ofType(FEED_POPULATE_TYPE),
      concatMap((action: FeedAction) => {
        const group = action.payload.group;
        return this.getMatches(group).pipe(
          map((response) =>
            FeedUpdate({
              group,
              update: {
                currentMatch: get(response, 'matches[0]'),
                currentMatchFeedbackSubmissions: get(
                  response,
                  'feedbackSubmissions',
                  [],
                ),
                isCurrentMatchLoading: false,
              },
            }),
          ),
        );
      }),
    ),
  );

  populateOtherMatches = createEffect(() =>
    this.actions.pipe(
      ofType(FEED_UPDATE_TYPE),
      filter((action: FeedAction) =>
        isEqual(action.payload.update.isCurrentMatchLoading, false),
      ),
      concatMap((action: FeedAction) => {
        const group = action.payload.group;
        const { currentMatch } = action.payload.update;
        return this.getAllMatches(group, currentMatch).pipe(
          map((response) =>
            FeedUpdate({
              group,
              update: {
                feedbacks: response.feedbacks,
                isPastMatchesLoading: false,
                matches: this.filterUnwantedMatches(response.matches),
              },
            }),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions: Actions,
    public accountBackend: AccountBackend,
  ) {}

  public getMatches(group: Group) {
    const defaultResult = of({ feedbackSubmissions: {}, matches: [] });
    if (!group.matchmaking_enabled) {
      return defaultResult;
    }
    const matchesSource = this.accountBackend
      .getAccountGroupsMatches(group)
      .pipe(
        map((matches: Match[] = []) => this.filterUnwantedMatches(matches)),
      );
    return matchesSource.pipe(
      concatMap((matches: Match[] = []) => {
        if (isEmpty(matches)) {
          return defaultResult;
        }
        return this.accountBackend.v2
          .getAccountGroupsMatchFeedbackSubmissions(group, matches[0])
          .pipe(
            catchError(() => of({})),
            map((response = {}) => ({
              matches,
              feedbackSubmissions: get(response, 'feedbackSubmissions', {}),
            })),
          );
      }),
      catchError(() => defaultResult),
    );
  }

  public getAllMatches(group: Group, match: Match = {}) {
    const currentMatchId = match._id;
    const params = {
      limit: 100,
      ...(currentMatchId && { excludeIds: [currentMatchId] }),
    };
    return this.accountBackend.v3.getAccountGroupsMatches(group, params).pipe(
      concatMap(({ matches = [] }: { matches: Match[] }) => {
        const filteredMatches = this.filterUnwantedMatches(matches)
          .sort(
            (a, b) =>
              new Date(a.cycle_end).getTime() - new Date(b.cycle_end).getTime(),
          )
          .reverse();
        const filteredMatchIds = filteredMatches.map(({ _id }) => _id);
        return this.accountBackend.v2
          .getAccountGroupsMatchFeedbacks(group, {
            feedbackTypes: [
              'match/complete',
              'match/complete/have-met',
              'match/complete/havent-met',
            ],
            matchIds: [currentMatchId, ...filteredMatchIds],
          })
          .pipe(
            map((response: { feedbacks?: Feedback[] } = {}) => {
              return {
                matches: filteredMatches,
                ...response,
              };
            }),
          );
      }),
    );
  }

  public getAccountMatchCycles(group: Group, matchConfig: MatchConfig) {
    return this.accountBackend
      .getAccountGroupsMatchCycles(group, {
        matchConfig: matchConfig._id,
        cycleEndMin: new Date().toISOString(),
        cycleStartMax: new Date().toISOString(),
      })
      .pipe(catchError(() => EMPTY));
  }

  public getCurrentMatchConfig(group: Group) {
    return this.accountBackend
      .getAccountGroupsMatchConfig(group, { status: ['active', 'upcoming'] })
      .pipe(
        catchError(() => EMPTY),
        map(
          (matchConfigsResponse: { items: MatchConfig[] }) =>
            matchConfigsResponse,
        ),
      );
  }

  filterUnwantedMatches(matches: Match[]) {
    return matches
      .filter((match) =>
        match.accounts.every(
          (matchAccount) => !isEqual('deactivated', matchAccount.status),
        ),
      )
      .filter(({ program }) => isEmpty(program));
  }
}
